import { Attr, BelongsTo, HasMany, HasOne, Model } from 'spraypaint';

import { ApplicationRecord } from './application_record';
import AvailabilityZone from './availability_zone';
import Client from './client';
import Facility from './facility';
import Image from './image';
import InstanceType from './instance_type';
import Keypair from './keypair';
import SecurityGroup from './security_group';
import Subnet from './subnet';
import TemplateInstanceTypeAssignment from './template_instance_type_assignment';
import TemplateSecurityGroupAssignment from './template_security_group_assignment';
import Vpc from './vpc';
import ChefPackage from './chef_package';
import ImagePipeline from './image_pipeline';

@Model()
export default class Template extends ApplicationRecord {
  static jsonapiType = 'templates';
  @Attr() name: string;
  @Attr() shortName: string;
  @Attr() createdAt: string;
  @Attr() updatedAt: string;

  @Attr() maneAgentInstallerPath: string;
  @Attr() ebsVolumeSize: number;
  @Attr() dataVolumeSize: number;
  @Attr() multiAz: boolean;
  @Attr() ebsRaid: boolean;
  @Attr() render: boolean;
  @Attr() infrastructure: boolean;
  @Attr() enableGp3: boolean;
  @Attr() gp3Throughput: number;
  @Attr() gp3Iops: number;
  @Attr() enableEncryption: boolean;
  @Attr() useLatestImage: boolean;
  @Attr() enableTimezone: boolean;
  @Attr() enableParsec: boolean;
  @Attr() parsecServerNetworkStartPort: string;
  @Attr() autoRebuild: boolean;
  @Attr() maintenanceWindow: boolean;
  @Attr() kmsKeyId: string;
  @Attr() enableInstanceProtection: boolean;
  @Attr() createEni: boolean;
  @Attr() joinDomain: boolean;
  @Attr() customTags: object;
  @Attr() vdiProtocol: string;
  @Attr() enablePcoipUltra: string;
  @Attr() pcoipAgentType: string;
  @Attr() lastImageName: string;
  @Attr() lastImageIdentifier: string;
  @Attr() placementGroup: string;
  @Attr({ persist: false }) hourlyInstanceCost: number;
  @Attr({ persist: false }) storageCost: number;
  @Attr({ persist: false }) facilityId: string;
  @Attr({ persist: false }) selected: boolean;
  @Attr() runList: string[];
  @Attr() persistOnRunListFailure: boolean;

  @HasOne() chefPackage: ChefPackage;

  @BelongsTo() client: Client;
  @BelongsTo() facility: Facility;

  @HasOne() image: Image;
  @HasOne() imagePipeline: ImagePipeline;
  @HasOne() instanceType: InstanceType;
  @HasOne() keypair: Keypair;
  @HasOne() availabilityZone: AvailabilityZone;
  @HasOne() vpc: Vpc;
  @HasOne() subnet: Subnet;
  @HasMany()
  templateInstanceTypeAssignments: TemplateInstanceTypeAssignment[];

  @HasMany()
  templateSecurityGroupAssignments: TemplateSecurityGroupAssignment[];

  @HasMany() securityGroups: SecurityGroup[];

  constructor(attrs?: Record<string, any>) {
    super(attrs);
    this.selected = false;
  }

  isParsec() {
    return this.enableParsec || this.vdiProtocol === 'parsec';
  }

  isTeradici() {
    return this.vdiProtocol === 'pcoip';
  }

  isNiceDcv() {
    return this.vdiProtocol === 'nice_dcv';
  }

  protocolName() {
    switch (this.vdiProtocol) {
      case 'parsec':
        return 'Parsec';
      case 'pcoip':
        return 'HP Anyware';
      case 'nice_dcv':
        return 'DCV';
      default:
        return '-';
    }
  }

  shortProtocolName() {
    switch (this.vdiProtocol) {
      case 'parsec':
        return '(Parsec)';
      case 'pcoip':
        return '(HP Anyware)';
      case 'nice_dcv':
        return '(DCV)';
      default:
        return '';
    }
  }

  totalCost(hoursUsed: number): number {
    if (!this.hourlyInstanceCost || !this.storageCost) return 0;
    return (
      this.hourlyInstanceCost * hoursUsed +
      parseFloat(this.storageCost.toString())
    );
  }

  orderedInstanceTypes(): InstanceType[] {
    const assignments = this.templateInstanceTypeAssignments || [];
    if (assignments.length > 0) {
      return assignments
        .slice()
        .sort((a, b) => a.position - b.position)
        .map(a => a.instanceType)
        .filter((it): it is InstanceType => Boolean(it));
    }
    return this.instanceType ? [this.instanceType] : [];
  }
}
